import React, { useState, useEffect } from 'react';
import domain from '../../../../reusecomponent/domain';
import { getToken } from '../../../function/token';
import { ErrorToastContainer, SuccessToastContainer } from '../../../function/CustomFunction';

export default function VideoModal({ open, onClose, node, onSave }) {
  const [formData, setFormData] = useState({
    sendMethod: 'upload', // 'upload' or 'custom'
    videoUrl: '',
    delay: 0,
    customField: ''
  });
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);

  useEffect(() => {
    if (open && node) {
      setFormData({
        sendMethod: node.data.sendMethod || 'upload',
        videoUrl: node.data.videoUrl || '',
        delay: node.data.delay || 0,
        customField: node.data.customField || ''
      });
    }
  }, [open, node]);

  const handleSave = () => {
    if (onSave) {
      onSave(node.id, formData);
    }
    onClose();
  };

  const handleCancel = () => {
    // Reset form data to original values when canceling
    if (node) {
      setFormData({
        sendMethod: node.data.sendMethod || 'upload',
        videoUrl: node.data.videoUrl || '',
        delay: node.data.delay || 0,
        customField: node.data.customField || ''
      });
    }
    onClose();
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragIn = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragActive(true);
    }
  };

  const handleDragOut = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith('video/')) {
        uploadFile(file);
      } else {
        ErrorToastContainer('Please drop a valid video file');
      }
    }
  };

  const handleContentClick = (e) => {
    e.stopPropagation();
  };

  // File upload function
  const uploadFile = async (file) => {
    if (!file) return;

    setUploading(true);
    try {
      const token = getToken();
      if (!token) {
        ErrorToastContainer('Authentication token not found');
        setUploading(false);
        return;
      }

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${domain()}/campaign/uplode-file`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.status) {
        // Update the form data with the uploaded file URL
        setFormData(prev => ({
          ...prev,
          videoUrl: result.data || result.url || result.link || '',
          sendMethod: 'upload'
        }));
        setUploadedFile(file);
        SuccessToastContainer('Video uploaded successfully!');
      } else {
        ErrorToastContainer(result.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      ErrorToastContainer('Failed to upload video. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Handle file selection from input
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type.startsWith('video/')) {
        uploadFile(file);
      } else {
        ErrorToastContainer('Please select a valid video file');
      }
    }
  };

  if (!open) return null;

  const modalStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    zIndex: 10000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  };

  const contentStyle = {
    background: '#ffffff',
    borderRadius: '8px',
    padding: '24px',
    minWidth: '600px',
    maxWidth: '700px',
    maxHeight: '80vh',
    overflowY: 'auto',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
  };

  const inputStyle = {
    width: '100%',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    padding: '8px 12px',
    fontSize: '14px',
    marginBottom: '12px',
    outline: 'none'
  };

  const buttonStyle = {
    background: '#007bff',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    fontSize: '14px',
    cursor: 'pointer',
    marginRight: '8px'
  };

  const secondaryButtonStyle = {
    background: '#6c757d',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    fontSize: '14px',
    cursor: 'pointer'
  };

  const infoBoxStyle = {
    background: '#e8f5e8',
    border: '1px solid #c3e6c3',
    borderRadius: '6px',
    padding: '16px',
    marginBottom: '20px'
  };

  const uploadAreaStyle = {
    border: '2px dashed #d1d5db',
    borderRadius: '8px',
    padding: '40px 20px',
    textAlign: 'center',
    background: dragActive ? '#f8f9fa' : '#ffffff',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    marginBottom: '16px'
  };

  return (
    <div style={modalStyle} onClick={handleCancel}>
      <div style={contentStyle} onClick={handleContentClick}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, color: '#495057', fontSize: '20px', fontWeight: '600' }}>
            Configure Video
          </h2>
        </div>

        {/* Choose How to Send Media Section */}
        <div style={infoBoxStyle}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600', color: '#28a745' }}>
            Choose How to Send Video
          </h3>
          <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#495057' }}>
            You can deliver video to users in two ways:
          </p>
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontWeight: '600', color: '#28a745', marginBottom: '4px' }}>
              Use Custom Field
            </div>
            <div style={{ fontSize: '13px', color: '#666' }}>
              Select this option if the video URL is already stored in a custom field. This is useful for dynamic or previously uploaded content.
            </div>
          </div>
          <div>
            <div style={{ fontWeight: '600', color: '#28a745', marginBottom: '4px' }}>
              Upload New Video
            </div>
            <div style={{ fontSize: '13px', color: '#666' }}>
              Upload a new video file directly from your device. This video will be sent with the message immediately upon upload.
            </div>
          </div>
        </div>

        {/* Send Method Selection */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <button
              style={{
                ...buttonStyle,
                background: formData.sendMethod === 'custom' ? '#28a745' : '#f8f9fa',
                color: formData.sendMethod === 'custom' ? '#ffffff' : '#495057',
                border: '1px solid #d1d5db'
              }}
              onClick={() => setFormData(prev => ({ ...prev, sendMethod: 'custom' }))}
            >
              Use Custom Field
            </button>
            <button
              style={{
                ...buttonStyle,
                background: formData.sendMethod === 'upload' ? '#28a745' : '#f8f9fa',
                color: formData.sendMethod === 'upload' ? '#ffffff' : '#495057',
                border: '1px solid #d1d5db'
              }}
              onClick={() => setFormData(prev => ({ ...prev, sendMethod: 'upload' }))}
            >
              Upload New Video
            </button>
          </div>
        </div>

                 {/* Upload Area or Custom Field Input */}
         {formData.sendMethod === 'upload' ? (
           <div
             style={uploadAreaStyle}
             onDragEnter={handleDragIn}
             onDragLeave={handleDragOut}
             onDragOver={handleDrag}
             onDrop={handleDrop}
             onClick={() => !uploading && document.getElementById('video-upload').click()}
           >
             {uploading ? (
               <div>
                 <div style={{ fontSize: '48px', color: '#28a745', marginBottom: '12px' }}>
                   ⏳
                 </div>
                 <div style={{ fontSize: '16px', color: '#495057', marginBottom: '8px' }}>
                   Uploading video...
                 </div>
                 <div style={{ fontSize: '13px', color: '#6c757d' }}>
                   Please wait while we upload your video
                 </div>
               </div>
             ) : uploadedFile ? (
               <div>
                 <div style={{ fontSize: '48px', color: '#28a745', marginBottom: '12px' }}>
                   ✅
                 </div>
                 <div style={{ fontSize: '16px', color: '#495057', marginBottom: '8px' }}>
                   Video uploaded successfully!
                 </div>
                 <div style={{ fontSize: '13px', color: '#6c757d' }}>
                   {uploadedFile.name}
                 </div>
                 <button
                   onClick={(e) => {
                     e.stopPropagation();
                     setUploadedFile(null);
                     setFormData(prev => ({ ...prev, videoUrl: '' }));
                   }}
                   style={{
                     background: '#dc3545',
                     color: '#ffffff',
                     border: 'none',
                     borderRadius: '4px',
                     padding: '4px 8px',
                     fontSize: '12px',
                     cursor: 'pointer',
                     marginTop: '8px'
                   }}
                 >
                   Remove
                 </button>
               </div>
             ) : (
               <div>
                 <div style={{ fontSize: '48px', color: '#28a745', marginBottom: '12px' }}>
                   🎥
                 </div>
                 <div style={{ fontSize: '16px', color: '#495057', marginBottom: '8px' }}>
                   Click to upload or drag and drop
                 </div>
                 <div style={{ fontSize: '13px', color: '#6c757d' }}>
                   Supported types: mp4, mov, avi
                 </div>
               </div>
             )}
             <input
               id="video-upload"
               type="file"
               accept="video/mp4,video/quicktime,video/avi"
               style={{ display: 'none' }}
               onChange={handleFileSelect}
               disabled={uploading}
             />
           </div>
        ) : (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#495057' }}>
              Custom Field
            </label>
            <select
              value={formData.customField}
              onChange={e => setFormData(prev => ({ ...prev, customField: e.target.value }))}
              style={inputStyle}
            >
              <option value="">Select custom field</option>
              <option value="video_url">Video URL</option>
              <option value="product_video">Product Video</option>
              <option value="demo_video">Demo Video</option>
              <option value="promotional_video">Promotional Video</option>
            </select>
          </div>
        )}

                 {/* Custom URL Input */}
         <div style={{ marginBottom: '20px' }}>
           <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
             <span style={{ fontSize: '14px', color: '#495057' }}>Video URL</span>
             <span style={{ fontSize: '16px' }}>🔗</span>
             <span style={{ fontSize: '12px' }}>▼</span>
           </div>
           <input
             type="text"
             value={formData.videoUrl}
             onChange={e => setFormData(prev => ({ ...prev, videoUrl: e.target.value }))}
             placeholder={formData.sendMethod === 'upload' ? 
               "Video URL will appear here after upload" : 
               "Enter video URL or select custom field"
             }
             style={{
               ...inputStyle,
               backgroundColor: formData.sendMethod === 'upload' && formData.videoUrl ? '#e8f5e8' : '#ffffff'
             }}
             readOnly={formData.sendMethod === 'upload' && uploadedFile}
           />
           {formData.sendMethod === 'upload' && formData.videoUrl && (
             <div style={{ 
               fontSize: '12px', 
               color: '#28a745', 
               marginTop: '4px',
               padding: '4px 8px',
               background: '#e8f5e8',
               borderRadius: '4px',
               border: '1px solid #c3e6c3'
             }}>
               ✅ Uploaded video URL: {formData.videoUrl.length > 50 ? 
                 formData.videoUrl.substring(0, 50) + '...' : 
                 formData.videoUrl
               }
             </div>
           )}
         </div>

        {/* Delay Setting */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#495057' }}>
            Delay in reply - {formData.delay} sec
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <input
              type="range"
              min="0"
              max="30"
              value={formData.delay}
              onChange={e => setFormData(prev => ({ ...prev, delay: parseInt(e.target.value) }))}
              style={{ flex: 1 }}
            />
            <span style={{ fontSize: '14px', color: '#6c757d', minWidth: '50px' }}>
              {formData.delay} sec
            </span>
          </div>
        </div>

                 {/* Action Buttons */}
         <div style={{ display: 'flex', justifyContent: 'space-between' }}>
           <button
             onClick={handleSave}
             disabled={uploading}
             style={{ 
               ...buttonStyle, 
               background: uploading ? '#6c757d' : '#28a745', 
               display: 'flex', 
               alignItems: 'center', 
               gap: '6px',
               cursor: uploading ? 'not-allowed' : 'pointer'
             }}
           >
             {uploading ? '⏳ Uploading...' : '💾 Save'}
           </button>
           <button
             onClick={handleCancel}
             disabled={uploading}
             style={{ 
               ...secondaryButtonStyle, 
               display: 'flex', 
               alignItems: 'center', 
               gap: '6px',
               cursor: uploading ? 'not-allowed' : 'pointer'
             }}
           >
             ✕ Cancel
           </button>
         </div>
      </div>
    </div>
  );
}
